import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Search,
  Filter,
  ExternalLink,
  FileText,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
type SearchScope = 'all' | 'internal' | 'web';

interface InternalResult {
  document_id: string;
  document_name: string;
  excerpt: string;
  similarity: number;
}

interface WebResult {
  title: string;
  url: string;
  snippet: string;
}

interface SearchResponse {
  synthesis: string;
  internal_results: InternalResult[];
  web_results: WebResult[];
}

const FILE_TYPE_OPTIONS = ['All', 'PDF', 'DOCX', 'XLSX', 'PPTX', 'TXT'] as const;
const DATE_RANGE_OPTIONS = ['Any time', 'Last 7 days', 'Last 30 days', 'Last year'] as const;

export function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<SearchScope>('all');
  const [fileType, setFileType] = useState<string>('All');
  const [dateRange, setDateRange] = useState<string>('Any time');
  const [project, setProject] = useState<string>('All');

  const searchMutation = useMutation({
    mutationFn: async (q: string) => {
      const { data, error } = await supabase.functions.invoke('search', {
        body: {
          query: q,
          scope,
          filters: {
            file_type: fileType !== 'All' ? fileType.toLowerCase() : undefined,
            date_range: dateRange !== 'Any time' ? dateRange : undefined,
            project: project !== 'All' ? project : undefined,
          },
        },
      });
      if (error) throw error;
      return data as SearchResponse;
    },
  });

  const results = searchMutation.data;

  const handleSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    searchMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      <PageHeader title="Search" description="Search across documents and the web" />

      <div className="flex-1 overflow-y-auto px-6 pb-8">
        {/* Search bar */}
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search anything..."
                className="h-11 pl-10 text-base"
              />
            </div>
            <Select value={scope} onValueChange={(v) => setScope(v as SearchScope)}>
              <SelectTrigger className="h-11 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="web">Web</SelectItem>
              </SelectContent>
            </Select>
            <Button
              className="h-11"
              onClick={handleSearch}
              disabled={!query.trim() || searchMutation.isPending}
            >
              {searchMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Search
            </Button>
          </div>

          {/* Filter chips (Internal scope only) */}
          {scope !== 'web' && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={fileType} onValueChange={setFileType}>
                <SelectTrigger className="h-7 w-24 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILE_TYPE_OPTIONS.map((ft) => (
                    <SelectItem key={ft} value={ft}>{ft}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="h-7 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGE_OPTIONS.map((dr) => (
                    <SelectItem key={dr} value={dr}>{dr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={project} onValueChange={setProject}>
                <SelectTrigger className="h-7 w-24 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Loading state */}
        {searchMutation.isPending && (
          <div className="mx-auto mt-8 max-w-4xl space-y-4">
            <Skeleton className="h-28 w-full" />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && !searchMutation.isPending && (
          <div className="mx-auto mt-8 max-w-4xl space-y-6">
            {/* AI Synthesis */}
            {results.synthesis && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Sparkles className="h-4 w-4 text-primary" />
                    AI Synthesis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed">{results.synthesis}</p>
                </CardContent>
              </Card>
            )}

            {/* Two-column results */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Internal Results */}
              {scope !== 'web' && (
                <div className="space-y-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <FileText className="h-4 w-4" />
                    Internal Results
                    {results.internal_results.length > 0 && (
                      <Badge variant="secondary" className="text-[10px]">
                        {results.internal_results.length}
                      </Badge>
                    )}
                  </h3>
                  {results.internal_results.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No internal results found</p>
                  ) : (
                    results.internal_results.map((result) => (
                      <InternalResultCard key={result.document_id} result={result} onOpen={() => navigate(`/documents/${result.document_id}`)} />
                    ))
                  )}
                </div>
              )}

              {/* Web Results */}
              {scope !== 'internal' && (
                <div className="space-y-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <ExternalLink className="h-4 w-4" />
                    Web Results
                    {results.web_results.length > 0 && (
                      <Badge variant="secondary" className="text-[10px]">
                        {results.web_results.length}
                      </Badge>
                    )}
                  </h3>
                  {results.web_results.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No web results found</p>
                  ) : (
                    results.web_results.map((result, idx) => (
                      <WebResultCard key={idx} result={result} />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -- Extracted result cards to keep page under line limit -- */

function InternalResultCard({ result, onOpen }: { result: InternalResult; onOpen: () => void }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-medium">{result.document_name}</h4>
          <Badge variant="outline" className="shrink-0 text-[10px]">
            {Math.round(result.similarity * 100)}%
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground line-clamp-3">{result.excerpt}</p>
        <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={onOpen}>
          Open
        </Button>
      </CardContent>
    </Card>
  );
}

function WebResultCard({ result }: { result: WebResult }) {
  return (
    <Card>
      <CardContent className="p-4">
        <a
          href={result.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-primary hover:underline"
        >
          {result.title}
        </a>
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{result.url}</p>
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{result.snippet}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2 h-7 text-xs"
          onClick={() => window.open(result.url, '_blank', 'noopener,noreferrer')}
        >
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          Open
        </Button>
      </CardContent>
    </Card>
  );
}
